// frontend/src/pages/ImportedProductsPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TableSortLabel
} from '@mui/material';
import { api } from '../utils/api';
import { toast } from 'react-toastify';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const ImportedProductsPage = () => {
  const [syncing, setSyncing] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchProducts = useCallback(async (currentStoreId) => {
    if (!currentStoreId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/imported-products/${currentStoreId}`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const storeRes = await api.get('/api/stores');
        const storeData = await storeRes.json();
        if (storeData && storeData.length > 0) {
          const firstStoreId = storeData[0].id;
          setStoreId(firstStoreId);
          await fetchProducts(firstStoreId);
        } else {
          console.warn('No store found for the user.');
          toast.warn('No Shopify store connected.');
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Failed to load initial page data.');
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [fetchProducts]);

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedProducts(selectedProducts.length === products.length ? [] : products.map(p => p.id));
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return products;
    return [...products].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [products, sortConfig]);

  const handleSync = async () => {
    if (!storeId) {
      toast.error('Cannot sync without a store ID.');
      return;
    }
    setSyncing(true);
    toast.info('Starting product sync with Shopify...');
    try {
      const syncResponse = await api.get(`/api/shopify/sync-products/${storeId}`);
      const syncData = await syncResponse.json();
      toast.success(`Sync complete! ${syncData.syncedProducts} products updated/added.`);
      // Refresh the product list after sync
      fetchProducts(storeId);
    } catch (error) {
      console.error('Error syncing products:', error);
      const errorMessage = error.response?.data?.message || 'Failed to sync products from Shopify.';
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Products...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Imported Products
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={syncing ? <CircularProgress size={20} /> : <SystemUpdateAltIcon />}
            onClick={handleSync}
            disabled={syncing || !storeId}
          >
            {syncing ? 'Syncing...' : 'Sync from Shopify'}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length}
                    checked={products.length > 0 && selectedProducts.length === products.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'title'}
                    direction={sortConfig.key === 'title' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('title')}
                  >
                    Product Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>Image</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'createdAt'}
                    direction={sortConfig.key === 'createdAt' ? sortConfig.direction : 'asc'}
                    onClick={() => requestSort('createdAt')}
                  >
                    Imported At
                  </TableSortLabel>
                </TableCell>
                <TableCell>Taobao ID</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No products imported yet.
                  </TableCell>
                </TableRow>
              ) : (
                sortedProducts.map((p) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={p.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedProducts.indexOf(p.id) !== -1}
                        onChange={() => toggleProductSelection(p.id)}
                      />
                    </TableCell>
                    <TableCell>{p.title || '—'}</TableCell>
                    <TableCell>
                      <img
                        src={p.imageUrl || '/placeholder.png'}
                        alt={p.title || 'Imported Product'}
                        style={{ height: 64, width: 64, objectFit: 'cover', borderRadius: '4px' }}
                      />
                    </TableCell>
                    <TableCell>{new Date(p.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{p.taobaoProductId}</TableCell>
                    <TableCell>
                      <Button
                        variant="text"
                        href={`https://item.taobao.com/item.htm?id=${p.taobaoProductId}`}
                        target="_blank"
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ImportedProductsPage;
